import type { DashboardLayoutBlob, DashboardLayoutComponent, DashboardLayoutConnector } from '../../types/api/dashboard/DashboardLayout';
import { isRecord } from '../object';

export type SerializeDashboardLayoutResult = {
    layout: DashboardLayoutBlob;
    /** What the board carried that the persisted shape cannot say at all. */
    problems: string[];
};

/** Turns a live board's options into the shape the platform persists */
export function serializeDashboardLayout(boardOptions: unknown): SerializeDashboardLayoutResult {
    if (!isRecord(boardOptions)) {
        return { layout: {}, problems: ['the board reported no options'] };
    }

    const problems: string[] = [];
    const gui = serializeGui(boardOptions.gui, problems);
    const components = serializeComponents(boardOptions.components, problems);
    const connectors = serializeConnectors(boardOptions.dataPool);

    return {
        layout: {
            ...(gui === undefined ? {} : { gui }),
            components,
            dataPool: { connectors },
        },
        problems,
    };
}

/** The board's cell geometry */
function serializeGui(rawGui: unknown, problems: string[]): unknown {
    if (rawGui === undefined) {
        return undefined;
    }

    const plainGui = copyAsPlainJson(rawGui);
    if (plainGui === undefined) {
        problems.push('the board layout could not be written as JSON');
    }
    return plainGui;
}

/** Every placed component, in board order. */
function serializeComponents(rawComponents: unknown, problems: string[]): DashboardLayoutComponent[] {
    if (!Array.isArray(rawComponents)) {
        return [];
    }

    const components: DashboardLayoutComponent[] = [];

    for (const [index, rawComponent] of rawComponents.entries()) {
        const componentNumber = String(index + 1);

        const plainComponent = copyAsPlainJson(rawComponent);
        if (!isRecord(plainComponent)) {
            problems.push(`component ${componentNumber} could not be stored`);
            continue;
        }

        const renderTo = plainComponent.renderTo;
        if (typeof renderTo !== 'string' || renderTo === '') {
            problems.push(`component ${componentNumber} sits in no cell`);
            continue;
        }

        components.push(plainComponent);
    }

    return components;
}

/** The pool as the board holds it — which connectors the stored board needs is `compileDataConnectors`' call. */
function serializeConnectors(rawDataPool: unknown): DashboardLayoutConnector[] {
    if (!isRecord(rawDataPool) || !Array.isArray(rawDataPool.connectors)) {
        return [];
    }

    const connectors: DashboardLayoutConnector[] = [];
    for (const rawConnector of rawDataPool.connectors) {
        const plainConnector = copyAsPlainJson(rawConnector);
        if (isRecord(plainConnector)) {
            connectors.push(plainConnector);
        }
    }
    return connectors;
}

/** Functions and other values JSON cannot carry are dropped */
function copyAsPlainJson(value: unknown): unknown {
    try {
        const plainValue: unknown = JSON.parse(JSON.stringify(value));
        return plainValue;
    } catch {
        return undefined;
    }
}
